import { useEffect, useRef, useState } from 'preact/hooks';
import { useWidgetSession } from './hooks/useWidgetSession';
import { useThreads } from './hooks/useThreads';
import { usePicker } from './hooks/usePicker';
import { useMarkers } from './hooks/useMarkers';
import type { LocalComment } from './storage';
import { dashboardOriginFrom } from './lib/origin';
import { isOwnComment } from './lib/comments';
import type { Active } from './lib/types';
import type { HostAdapter } from '@pinmerge/shared';
import { PinPopover } from './components/PinPopover';
import { MarkerLayer } from './components/MarkerLayer';
import { FabButton } from './components/FabButton';
import { t } from './i18n';

interface BootConfig {
  token: string;
  apiUrl: string;
  demoMode: boolean;
  /** URL JWT delivered via ?pinmerge_auth=… by the magic-link callback. The
   *  widget exchanges it on mount to bind creatorToken -> reviewer profile. */
  initialAuthHandoffJwt: string | null;
  /** Prototype-version slug from ?pinmerge_v=. Stays stable for the session;
   *  parsed once at boot in main.ts. NULL when no param was sent — the server
   *  resolves to the project's default version (if any). */
  versionSlug: string | null;
}

interface AppProps {
  config: BootConfig;
  onExit: () => void;
  /** Host-application adapter, supplied by the package channel. Only threaded
   *  through to the hooks/capture for now — not consumed yet (wiring lands in a
   *  later WP). Undefined in the script-tag channel. */
  adapter?: HostAdapter;
}

// Resize a textarea to fit its content. CSS `min-height` keeps the empty
// state from collapsing below the visual baseline; this only ever expands.
function autosizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

export function App({ config, onExit, adapter }: AppProps) {
  const session = useWidgetSession(config);
  const { reviewer, loggedIn, canPickWithoutLogin, reviewerName, ownTokenRef } = session;

  const [active, setActive] = useState<Active>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const threads = useThreads({
    config,
    ownTokenRef,
    reviewerName,
    reviewer,
    onSessionInvalid: () => {
      session.clearSession();
    },
    setError,
    adapter,
  });
  const { comments, projectSlug, activeVersion, linkCopiedFor } = threads;

  const picker = usePicker({
    canPickWithoutLogin,
    idle: !active,
    apiUrl: config.apiUrl,
    previewAvatar: reviewer
      ? {
          id: reviewer.avatar_id,
          bg: reviewer.avatar_bg,
          name: reviewerName,
        }
      : null,
    onNoAuth: () => openLogin(),
    onPick: (draft) => {
      setActive({ kind: 'new', draft });
      setText('');
      setError(null);
    },
    adapter,
  });
  const { mode } = picker;

  const markers = useMarkers({ comments, active, ownTokenRef });
  const { scroll, hoveredId, setHoveredId } = markers;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  // Tracks whether we've already auto-opened a pin from a `?pinmerge_comment=<uuid>`
  // permalink. One-shot — subsequent comments updates don't re-open.
  const permalinkOpenedRef = useRef(false);
  // Reply composition: separate from `text` so the user can type a reply while
  // also viewing the parent without state collisions. Cleared on submit + on
  // pin close.
  const [replyText, setReplyText] = useState('');
  // Avatar slugs whose <img> failed to load — we render initials instead. Keeps
  // the marker readable even when an avatar SVG 404s (legacy slug, missing file,
  // or — as happened once — a misconfigured dashboard origin).
  const [failedAvatars, setFailedAvatars] = useState<Set<string>>(() => new Set());
  // FAB menu open/closed. One button → click toggles the menu; click-outside
  // and ESC close it. No hover-trigger to keep the interaction predictable on
  // touch.
  const [menuOpen, setMenuOpen] = useState(false);
  const fabRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // When set, that reply (by local id) is in edit mode and reuses `text`.
  // `null` = no reply being edited. Only one edit at a time.
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  // Reaction-picker open state — local id of the comment whose picker is open
  // (could be the active comment OR one of its replies). Click-outside closes.
  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  // Per-comment menu (Bearbeiten · Archivieren) open state. Same id model as the
  // picker — only one menu open at a time, click-outside closes.
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  // Auto-focus the compose/edit textarea whenever a draft pin opens or edit mode starts.
  // The HTML autofocus attribute only fires on first page parse; for dynamically mounted
  // textareas we have to call .focus() ourselves. When opening an existing comment for
  // viewing (not editing), the focus moves to the always-visible reply textarea so the
  // reviewer can chain replies without an extra click.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (active?.kind === 'new' || (active?.kind === 'view' && active.editing)) {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      } else if (active?.kind === 'view' && !active.editing) {
        replyTextareaRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [active]);

  // Auto-grow the textareas whenever their contents change. The shared `text`
  // state drives compose / edit-comment / edit-reply (whichever is mounted),
  // so resizing `textareaRef` covers all three. Reply composer uses its own ref.
  useEffect(() => {
    autosizeTextarea(textareaRef.current);
  }, [text, active]);
  useEffect(() => {
    autosizeTextarea(replyTextareaRef.current);
  }, [replyText, active]);

  // Auto-open the pin matching a `?pinmerge_comment=<uuid>` permalink — once,
  // after the server-merged comments arrive (so we have remoteIds to match against).
  useEffect(() => {
    if (permalinkOpenedRef.current) return;
    const uuid = new URLSearchParams(window.location.search).get('pinmerge_comment');
    if (!uuid) return;
    const target = comments.find((c) => c.remoteId === uuid);
    if (!target) return;
    permalinkOpenedRef.current = true;
    // If the matched row is a reply, open its parent (replies don't have pins).
    const parentLocalId = target.parentId ?? target.id;
    openExisting(parentLocalId);
  }, [comments]);

  // Re-render once a minute so relative time labels refresh (`vor 2 Minuten` →
  // `vor 3 Minuten`). Cheap; only runs while the widget is mounted.
  const [, setTimeTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTimeTick((t) => t + 1), 60_000);
    return () => clearInterval(i);
  }, []);

  // Esc closes anything — the chord shortcuts for entering picking mode live
  // inside usePicker.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click-outside for the FAB menu. Only attaches while the menu is open so we
  // don't churn document listeners. Uses composedPath() because the widget
  // lives inside a Shadow DOM — without it, document-level events retarget to
  // the shadow host and contains() returns false for every internal element,
  // causing the menu to close before its own onClick can fire.
  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      const path = e.composedPath();
      const fab = fabRef.current;
      const menu = menuRef.current;
      const insideFab = fab && path.includes(fab);
      const insideMenu = menu && path.includes(menu);
      if (insideFab || insideMenu) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  // Close the open view-pin when the user clicks anywhere outside it. Only runs
  // for view mode (not compose/edit) so we never silently discard unsaved text.
  // Marker clicks are excluded so clicking another pin still switches instead
  // of close-then-reopen. We use composedPath so this works through Shadow DOM.
  useEffect(() => {
    if (active?.kind !== 'view' || active.editing) return;
    const onDocClick = (e: MouseEvent) => {
      const path = e.composedPath();
      if (pinRef.current && path.includes(pinRef.current)) return;
      const onMarker = path.some(
        (n) => n instanceof Element && n.classList?.contains('marker'),
      );
      if (onMarker) return;
      // Keep picker running when the user dismisses a view-pin via outside
      // click — only ESC or the FAB toggle should exit pick mode.
      closeComposeKeepPicker();
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Close everything *including* picker — used for ESC, logout, comment
  // delete. The FAB toggle handles its own picker stop.
  function closeAll() {
    picker.stopPicking();
    closeComposeKeepPicker();
    setMenuOpen(false);
  }

  // Close compose popovers but leave pick mode running. Lets the reviewer
  // drop several pins in a row — only ESC or the FAB toggle exit picking.
  function closeComposeKeepPicker() {
    setActive(null);
    setText('');
    setReplyText('');
    setEditingReplyId(null);
    setPickerOpenFor(null);
    setMenuOpenFor(null);
    setError(null);
  }

  function openExisting(id: string) {
    const c = comments.find((x) => x.id === id);
    if (!c) return;
    setActive({ kind: 'view', id, editing: false });
    setText(c.text);
    setReplyText('');
    setEditingReplyId(null);
    setPickerOpenFor(null);
    setMenuOpenFor(null);
    setError(null);
  }

  function startEditing() {
    if (!active || active.kind !== 'view') return;
    const c = comments.find((x) => x.id === active.id);
    if (!c) return;
    if (!isOwnComment(c, ownTokenRef.current)) return;
    setText(c.text);
    setActive({ ...active, editing: true });
  }

  function saveDraft() {
    if (!active || active.kind !== 'new') return;
    if (!text.trim() || !reviewer) return;
    threads.saveDraft(active.draft, text);
    closeComposeKeepPicker();
    hapticTick();
  }

  function saveEdit() {
    if (!active || active.kind !== 'view' || !active.editing) return;
    if (!text.trim()) return;
    threads.saveEdit(active.id, text);
    setActive({ ...active, editing: false });
  }

  function remove(id: string) {
    if (!confirm(t('pin.confirm-archive'))) return;
    threads.remove(id);
    closeAll();
  }

  // Primary sign-in entry point. Same-tab navigation to the dashboard's
  // /widget-auth page, which either mints the URL handoff JWT inline (when
  // a dashboard session already exists) or detours via /login first. The
  // user lands back on this prototype URL with ?pinmerge_auth=<jwt>, which
  // main.ts strips and useWidgetSession exchanges on mount.
  function openLogin() {
    if (config.demoMode) return;
    const dashboardOrigin = dashboardOriginFrom(config.apiUrl);
    const url = new URL('/widget-auth', dashboardOrigin);
    url.searchParams.set('return', window.location.href);
    url.searchParams.set('token', config.token);
    window.location.href = url.toString();
  }

  function logout() {
    if (!confirm(t('fab.confirm-signout'))) return;
    session.clearSession();
    closeAll();
  }

  function handleReactionClick(commentId: string, remoteId: string, emoji: string) {
    if (config.demoMode) return;
    if (!loggedIn) {
      openLogin();
      return;
    }
    hapticTick();
    threads.toggleReaction(commentId, remoteId, emoji);
  }

  async function submitReply(parent: LocalComment) {
    if (!parent.remoteId || !reviewer) return;
    if (!replyText.trim()) return;
    const replyToSend = replyText;
    setReplyText('');
    await threads.submitReply(parent, replyToSend);
  }

  function startEditingReply(replyId: string) {
    const reply = comments.find((c) => c.id === replyId);
    if (!reply) return;
    if (!isOwnComment(reply, ownTokenRef.current)) return;
    setText(reply.text);
    setEditingReplyId(replyId);
  }

  async function saveReplyEdit() {
    if (!editingReplyId) return;
    if (!text.trim()) return;
    const id = editingReplyId;
    const value = text;
    setEditingReplyId(null);
    setText('');
    await threads.saveReplyEdit(id, value);
  }

  function removeReply(replyId: string) {
    if (!confirm(t('pin.confirm-archive-reply'))) return;
    threads.removeReply(replyId);
  }

  // ─── Reaction picker rendering ────────────────────────────────────────
  // Slack-style: existing reactions (count > 0) render inline as chips and
  // toggle on click. A `[😊+]` trigger sits next to them, hidden until hover —
  // click opens a popover with all 8 emojis to pick from.

  const launcherActive = mode === 'picking' || active !== null;
  const hasPending = !config.demoMode && comments.some((c) => !c.remoteId);

  function exitReview() {
    if (!confirm(t('fab.confirm-exit'))) return;
    onExit();
  }

  function openDashboard() {
    if (!projectSlug) return;
    const url = `${dashboardOriginFrom(config.apiUrl)}/projects/${projectSlug}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setMenuOpen(false);
  }

  function togglePicking() {
    setMenuOpen(false);
    if (launcherActive) {
      closeAll();
      return;
    }
    if (!canPickWithoutLogin) {
      openLogin();
      return;
    }
    picker.startPicking();
  }

  return (
    <>
      <FabButton
        fabRef={fabRef}
        menuRef={menuRef}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        launcherActive={launcherActive}
        hasPending={hasPending}
        commentCount={comments.length}
        loggedIn={loggedIn}
        demoMode={config.demoMode}
        reviewer={reviewer}
        projectSlug={projectSlug}
        activeVersion={activeVersion}
        onTogglePicking={togglePicking}
        onOpenDashboard={openDashboard}
        onOpenLogin={openLogin}
        onLogout={logout}
        onExitReview={exitReview}
      />

      {mode === 'picking' && (
        <div class="hint">
          {t('picker.hint-click')} · <kbd>Esc</kbd> {t('picker.hint-esc')}
        </div>
      )}

      <MarkerLayer
        comments={comments}
        active={active}
        ownToken={ownTokenRef.current}
        scroll={scroll}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        failedAvatars={failedAvatars}
        onAvatarFailed={(slug) =>
          setFailedAvatars((prev) => {
            if (prev.has(slug)) return prev;
            const next = new Set(prev);
            next.add(slug);
            return next;
          })
        }
        apiUrl={config.apiUrl}
        loggedIn={loggedIn}
        canPickWithoutLogin={canPickWithoutLogin}
        onMarkerClick={openExisting}
      />

      <PinPopover
        active={active}
        comments={comments}
        reviewer={reviewer}
        reviewerName={reviewerName}
        loggedIn={loggedIn}
        canPickWithoutLogin={canPickWithoutLogin}
        ownToken={ownTokenRef.current}
        projectSlug={projectSlug}
        linkCopiedFor={linkCopiedFor}
        apiUrl={config.apiUrl}
        scroll={scroll}
        error={error}
        pinRef={pinRef}
        text={text}
        setText={setText}
        textareaRef={textareaRef}
        replyText={replyText}
        setReplyText={setReplyText}
        replyTextareaRef={replyTextareaRef}
        editingReplyId={editingReplyId}
        setEditingReplyId={setEditingReplyId}
        pickerOpenFor={pickerOpenFor}
        setPickerOpenFor={setPickerOpenFor}
        menuOpenFor={menuOpenFor}
        setMenuOpenFor={setMenuOpenFor}
        failedAvatars={failedAvatars}
        onAvatarFailed={(slug) =>
          setFailedAvatars((prev) => {
            if (prev.has(slug)) return prev;
            const next = new Set(prev);
            next.add(slug);
            return next;
          })
        }
        setActive={setActive}
        onSaveDraft={saveDraft}
        onSaveEdit={saveEdit}
        onRemove={remove}
        onSubmitReply={submitReply}
        onSaveReplyEdit={saveReplyEdit}
        onRemoveReply={removeReply}
        onReactionClick={handleReactionClick}
        onCopyPermalink={threads.copyPermalink}
        onStartEditing={startEditing}
        onStartEditingReply={startEditingReply}
        onCloseAll={closeAll}
        onOpenLogin={openLogin}
      />
    </>
  );
}

// 10ms vibrate — Android browsers respect it, iOS no-ops, desktop no-ops.
// Used at "the action committed" moments (pin saved, reaction toggled) so
// reviewers on phones get the same physical confirmation system buttons give.
function hapticTick(): void {
  navigator.vibrate?.(10);
}

